import React from 'react';
import styled, { css } from 'styled-components';

import { BasketState } from '@front/common/src/ducks/common/basket';
import { formatPrice } from '@front/common/src/utils/formatting';

import { MembershipOptIn } from '@front/customer/src/modals/membership-paywall/utils';
import { useInstantTaxCreditCleaningFunnelVariant } from '@front/customer/src/hooks/use-instant-tax-credit-cleaning-funnel-variant';
import { PricesResponse } from '@front/customer/src/types/prices';

import { translate } from '@front/i18n';

import Text from '@front/web-ui/src/components/base-text/variants/text';
import Skeleton from '@front/web-ui/src/components/skeleton/skeleton';

import SelectablePriceCard, { SelectablePriceCardProps } from '../selectable-price-card';

const t = translate('modals.membership_paywall.price_cards');

const StyledCard = styled(SelectablePriceCard)<{
  $height: 'md' | 'lg';
}>`
  ${({ $height }) => css`
    height: ${$height === 'lg' ? 105 : 85}px;
  `};
  margin-bottom: 20px;
`;

const StyledPrices = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

type SelectableCleaningStandardPriceCardProps = Pick<SelectablePriceCardProps, 'onPress'> & {
  duration: BasketState['duration'];
  prices: PricesResponse | undefined;
  isLoadingPrices: boolean;
  optInChoice?: MembershipOptIn;
};

const SelectableCleaningStandardPriceCard = ({
  duration,
  onPress,
  prices,
  isLoadingPrices,
  optInChoice,
}: SelectableCleaningStandardPriceCardProps) => {
  const showTaxReductions = useInstantTaxCreditCleaningFunnelVariant({ universe: 'cleaning' });

  const showPrice = !!duration;

  return (
    <StyledCard
      $height={showTaxReductions ? 'lg' : 'md'}
      isSelected={optInChoice === 'noMembership'}
      onPress={onPress}
      title={t('.standard.pay_without_club')}
      value='noMembership'
    >
      {showPrice && (
        <StyledPrices>
          {isLoadingPrices ? (
            <Skeleton linesWidth={showTaxReductions ? ['70px', '90px'] : ['70px']} />
          ) : (
            <>
              <Text.MDBold>{formatPrice(prices?.standard.after_tax.price)}</Text.MDBold>
              {showTaxReductions && (
                <>
                  <Text>
                    {t('.cleaning.or_price', { priceBeforeITC: formatPrice(prices?.standard.total.price) })}
                  </Text>
                  <Text.XXS>{t('.cleaning.before_itc')}</Text.XXS>
                </>
              )}
            </>
          )}
        </StyledPrices>
      )}
    </StyledCard>
  );
};

export default SelectableCleaningStandardPriceCard;
